import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    FileText, Image, FolderSync, ExternalLink, Landmark, Home, Search,
    Folder, FolderOpen, List, ChevronRight, ChevronDown,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SuchAuswahl from "@/components/ui/such-auswahl";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { listDokumente, dokumentArten, dokumenteVerzeichnen } from "@/lib/api";

// Die Schluessel kommen aus dem Backend, die Saetze stehen hier: dort ist die
// Art eine Kennung, hier etwas, das ein Mensch liest.
const ART_TEXT = {
    teilungserklaerung: "Teilungserklärung",
    beschlusssammlung: "Beschlusssammlung",
    einladung_etv: "Einladung Eigentümerversammlung",
    protokoll: "Protokoll",
    wirtschaftsplan: "Wirtschaftsplan",
    jahresabrechnung: "Jahresabrechnung",
    hausgeld: "Hausgeld",
    nebenkostenabrechnung: "Nebenkostenabrechnung",
    mietanpassung: "Mietanpassung",
    mietvertrag: "Mietvertrag",
    mietbescheinigung: "Mietbescheinigung",
    kaution: "Kaution",
    uebergabeprotokoll: "Übergabe",
    kaufvertrag: "Kaufvertrag",
    grundbuch: "Grundbuch",
    grundsteuer: "Grundsteuer",
    darlehen: "Darlehen",
    finanzierungsantrag: "Finanzierungsantrag",
    energieausweis: "Energieausweis",
    versicherung: "Versicherung",
    grundriss: "Grundriss & Plan",
    rechnung: "Rechnung",
    schreiben: "Schreiben",
    foto: "Foto",
    sonstiges: "Sonstiges",
};
const artText = (a) => ART_TEXT[a] || a;

const SPHAERE_TEXT = {
    gemeinschaft: "Gemeinschaftseigentum",
    sondereigentum: "Sondereigentum",
};
const SPHAERE_TON = {
    gemeinschaft: "bg-sky-50 text-sky-800 border-sky-200",
    sondereigentum: "bg-amber-50 text-amber-800 border-amber-200",
};

const kb = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Die Dokumente einer Liegenschaft oder eines Objekts.
 *
 * Bewusst eine Komponente für beide: Die Frage ist dieselbe ("was liegt zu
 * dieser Sache vor"), nur das Ziel unterscheidet sich. Zwei fast gleiche
 * Listen würden auseinanderlaufen.
 *
 * Die Dateien werden NICHT hier angezeigt, sondern in Drive geöffnet. Dort
 * liegen sie, dort gelten die Rechte, dort arbeiten die Menschen.
 */
const Dokumente = ({ zielId, zielName, drivOrdnerId = null, aufLiegenschaft = false }) => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState({ sphaere: "", art: "", jahr: "", indexiert: "" });
    const [suche, setSuche] = useState("");
    const [auswahl, setAuswahl] = useState({ arten: [], jahre: [] });
    const [laedt, setLaedt] = useState(true);
    const [syncLaeuft, setSyncLaeuft] = useState(false);
    // Ordneransicht ist die Voreinstellung: So liegt der Bestand in Drive, und
    // der Ordner traegt einen Teil der Bedeutung -- "WEG/2023" sagt bei einer
    // Datei namens "Protokoll.pdf" erst, worum es geht.
    const [nachOrdnern, setNachOrdnern] = useState(true);
    // Gemerkt werden die ZUgeklappten, nicht die offenen: wer die Ansicht
    // oeffnet, soll seinen Bestand sehen wie bisher, nur gegliedert. Zuklappen
    // ist dann eine bewusste Handlung.
    const [zu, setZu] = useState(() => new Set());

    const laden = useCallback(async () => {
        if (!zielId) return;
        setLaedt(true);
        try {
            const p = { ziel_id: zielId };
            if (filter.sphaere) p.sphaere = filter.sphaere;
            if (filter.art) p.art = filter.art;
            if (filter.jahr) p.jahr = Number(filter.jahr);
            if (filter.indexiert) p.indexiert = filter.indexiert === "ja";
            setItems(await listDokumente(p));
        } catch { toast.error("Dokumente nicht geladen"); }
        finally { setLaedt(false); }
    }, [zielId, filter]);

    useEffect(() => { laden(); }, [laden]);
    useEffect(() => { dokumentArten().then(setAuswahl).catch(() => {}); }, []);

    // Die Suche laeuft ueber Name und Pfad und bleibt im Browser: bei ein paar
    // hundert Zeilen ist das schneller als eine Anfrage je Tastendruck.
    const sichtbar = useMemo(() => {
        const q = suche.trim().toLowerCase();
        if (!q) return items;
        return items.filter((d) =>
            (d.name || "").toLowerCase().includes(q)
            || (d.pfad || "").toLowerCase().includes(q)
            // Die Schlagworte sind der eigentliche Gewinn der Indexierung:
            // "Joußen" findet alles vom Verwalter, auch wenn sein Name in
            // keinem Dateinamen steht.
            || (d.schlagworte || []).some((w) => w.toLowerCase().includes(q))
            || (d.beschreibung || "").toLowerCase().includes(q));
    }, [items, suche]);

    const abgleichen = async () => {
        setSyncLaeuft(true);
        try {
            const probe = await dokumenteVerzeichnen({ liegenschaftId: zielId, apply: false });
            if (!probe.neu) {
                toast.info("Nichts Neues im Drive-Ordner");
                return;
            }
            if (!window.confirm(`${probe.neu} neue Dateien verzeichnen?\n\n`
                + Object.entries(probe.nach_art).slice(0, 6)
                    .map(([a, n]) => `· ${artText(a)}: ${n}`).join("\n"))) return;
            const r = await dokumenteVerzeichnen({ liegenschaftId: zielId, apply: true });
            toast.success(`${r.davon_geschrieben} Dokumente verzeichnet`);
            laden();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.error || "Abgleich fehlgeschlagen", { description: d?.detail });
        } finally { setSyncLaeuft(false); }
    };

    // Der Pfad kommt als "WEG/2023/Protokolle/x.pdf" und wird hier wieder zu
    // dem Baum, als der er in Drive liegt -- Ordner im Ordner, nicht ein
    // zusammengeklebter Pfad je Zeile. Wer in Drive sucht und wer hier sucht,
    // soll denselben Weg gehen.
    const baum = useMemo(() => {
        const knoten = (name, pfad) => ({ name, pfad, kinder: new Map(), dateien: [], anzahl: 0 });
        const wurzel = knoten("", "");
        sichtbar.forEach((d) => {
            const teile = (d.pfad || "").split("/").filter(Boolean);
            let k = wurzel;
            k.anzahl += 1;
            // Das letzte Stueck ist die Datei selbst, alles davor sind Ordner.
            teile.slice(0, -1).forEach((t) => {
                if (!k.kinder.has(t)) k.kinder.set(t, knoten(t, k.pfad ? `${k.pfad}/${t}` : t));
                k = k.kinder.get(t);
                // Die Zahl am Ordner zaehlt alles darunter mit: ein zugeklappter
                // Ordner soll verraten, wie viel in ihm steckt.
                k.anzahl += 1;
            });
            k.dateien.push(d);
        });
        const ordne = (k) => {
            k.dateien.sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"));
            k.kinder = new Map([...k.kinder.entries()]
                .sort((a, b) => a[0].localeCompare(b[0], "de")));
            k.kinder.forEach(ordne);
        };
        ordne(wurzel);
        return wurzel;
    }, [sichtbar]);

    const alleOrdner = useMemo(() => {
        const l = [];
        const geh = (k) => k.kinder.forEach((ki) => { l.push(ki.pfad); geh(ki); });
        geh(baum);
        return l;
    }, [baum]);

    // Wer sucht oder filtert, will das Ergebnis sehen und nicht erst
    // aufklappen muessen.
    const filtertGerade = !!suche.trim() || !!filter.art || !!filter.jahr
        || !!filter.sphaere || !!filter.indexiert;

    const istOffen = (ordner) => filtertGerade || !zu.has(ordner);
    const umschalten = (ordner) => setZu((vorher) => {
        const n = new Set(vorher);
        if (n.has(ordner)) n.delete(ordner); else n.add(ordner);
        return n;
    });
    const alleZu = zu.size >= alleOrdner.length && alleOrdner.length > 0;
    const alleUmschalten = () => setZu(alleZu ? new Set() : new Set(alleOrdner));

    const nachSphaere = useMemo(() => {
        const z = { gemeinschaft: 0, sondereigentum: 0, offen: 0 };
        items.forEach((d) => { z[d.sphaere || "offen"] = (z[d.sphaere || "offen"] || 0) + 1; });
        return z;
    }, [items]);

    // Eine Dateizeile, in beiden Ansichten dieselbe. ``tiefe`` ruckt sie unter
    // ihren Ordner, ``mitPfad`` blendet den Ordner als Unterzeile ein -- in der
    // flachen Liste noetig, im Baum ueberfluessig.
    const dateiZeile = (d, tiefe, mitPfad) => (
        <tr key={d.id} className="hover:bg-gray-50/60" data-testid={`dok-${d.id}`}>
            <td className="py-2 pr-3" style={{ paddingLeft: 12 + tiefe * 18 }}>
                <div className="flex items-start gap-2">
                    {(d.mime || "").startsWith("image/")
                        ? <Image size={13} className="text-gray-400 mt-0.5 shrink-0" />
                        : <FileText size={13} className="text-gray-400 mt-0.5 shrink-0" />}
                    <div className="min-w-0">
                        <div className="text-slate-900 truncate" title={d.name}>{d.name}</div>
                        {mitPfad && (
                            <div className="text-[11px] text-gray-400 truncate" title={d.pfad}>
                                {(d.pfad || "").split("/").slice(0, -1).join(" / ") || "—"}
                            </div>
                        )}
                        {!!(d.schlagworte || []).length && (
                            <div className="mt-1 flex flex-wrap gap-1">
                                {d.schlagworte.slice(0, 4).map((w) => (
                                    <span key={w}
                                          className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                        {w}
                                    </span>
                                ))}
                                {d.schlagworte.length > 4 && (
                                    <span className="text-[10px] text-gray-400"
                                          title={d.schlagworte.join(" · ")}>
                                        +{d.schlagworte.length - 4}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </td>
            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{artText(d.art)}</td>
            <td className="px-3 py-2">
                {d.sphaere ? (
                    <span className={`text-[11px] px-2 py-0.5 rounded border ${SPHAERE_TON[d.sphaere]}`}>
                        {SPHAERE_TEXT[d.sphaere]}
                    </span>
                ) : <span className="text-[11px] text-gray-400">offen</span>}
            </td>
            <td className="px-3 py-2 text-right tabular-nums text-gray-600">{d.jahr || "—"}</td>
            <td className="px-3 py-2 text-right tabular-nums text-gray-500">{kb(d.groesse)}</td>
            <td className="px-3 py-2 text-right">
                {d.link && (
                    <a href={d.link} target="_blank" rel="noreferrer"
                       className="text-gray-400 hover:text-slate-900 inline-flex"
                       aria-label="In Google Drive öffnen" title="In Google Drive öffnen">
                        <ExternalLink size={14} />
                    </a>
                )}
            </td>
        </tr>
    );

    // Der Baum wird zu einer flachen Folge von <tr> abgewickelt: eine Tabelle
    // vertraegt keine verschachtelten Zeilen, die Ebene steckt darum im
    // Einzug. Zugeklappte Ordner liefern ihre Kinder gar nicht erst.
    const zeilenFuer = (k, tiefe) => {
        const out = [];
        k.kinder.forEach((kind) => {
            const auf = istOffen(kind.pfad);
            out.push(
                <tr key={`o:${kind.pfad}`}
                    className="bg-gray-50/70 cursor-pointer hover:bg-gray-100/80"
                    onClick={() => umschalten(kind.pfad)}
                    data-testid={`dok-ordner-${kind.pfad}`}>
                    <td colSpan={6} className="py-1.5 pr-3"
                        style={{ paddingLeft: 12 + tiefe * 18 }}>
                        <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-700">
                            {auf ? <ChevronDown size={12} className="text-gray-400" />
                                 : <ChevronRight size={12} className="text-gray-400" />}
                            {auf ? <FolderOpen size={13} className="text-sky-600/70" />
                                 : <Folder size={13} className="text-sky-600/70" />}
                            {kind.name}
                            <span className="text-gray-400 font-normal">{kind.anzahl}</span>
                        </span>
                    </td>
                </tr>,
            );
            if (auf) out.push(...zeilenFuer(kind, tiefe + 1));
        });
        k.dateien.forEach((d) => out.push(dateiZeile(d, tiefe, false)));
        return out;
    };

    return (
        <div className="space-y-3" data-testid="dokumente">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                        {aufLiegenschaft ? <Landmark size={13} /> : <Home size={13} />}
                        {zielName}
                    </span>
                    <span>·</span>
                    <span>{items.length} Dokumente</span>
                    {nachSphaere.gemeinschaft > 0 && (
                        <span className="text-sky-700">{nachSphaere.gemeinschaft} Gemeinschaft</span>
                    )}
                    {nachSphaere.sondereigentum > 0 && (
                        <span className="text-amber-700">{nachSphaere.sondereigentum} Sondereigentum</span>
                    )}
                    <span>·</span>
                    <span title="Dokumente mit Schlagworten aus der Handindexierung">
                        {items.filter((d) => (d.schlagworte || []).length).length} indexiert
                    </span>
                    <span>·</span>
                    <span>{alleOrdner.length} Ordner</span>
                </div>
                {aufLiegenschaft && drivOrdnerId && (
                    <Button variant="outline" size="sm" className="h-8" disabled={syncLaeuft} grund="Abgleich läuft"
                            onClick={abgleichen} data-testid="dok-abgleich">
                        <FolderSync size={13} className="mr-1.5" />
                        Mit Drive abgleichen
                    </Button>
                )}
            </div>

            <div className="flex gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[180px]">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input className="h-8 pl-8" placeholder="Name, Ordner, Schlagwort …"
                           value={suche} onChange={(e) => setSuche(e.target.value)}
                           data-testid="dok-suche" />
                </div>
                <SuchAuswahl wert={filter.sphaere || "__alle__"} onWaehlen={(v) => setFilter({ ...filter, sphaere: v === "__alle__" ? "" : v })}
                    optionen={[
                        { wert: "__alle__", text: "Alle Sphären" },
                        { wert: "gemeinschaft", text: "Gemeinschaftseigentum" },
                        { wert: "sondereigentum", text: "Sondereigentum" },
                    ]}
                    className="w-[200px]"
                    platzhalter="Sphäre"
                    testId="dok-filter-sphaere"
                />
                <Select value={filter.art || "__alle__"}
                        onValueChange={(v) => setFilter({ ...filter, art: v === "__alle__" ? "" : v })}>
                    <SelectTrigger className="h-8 w-[190px]" data-testid="dok-filter-art">
                        <SelectValue placeholder="Art" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__alle__">Alle Arten</SelectItem>
                        {(auswahl.arten || []).map((a) => (
                            <SelectItem key={a} value={a}>{artText(a)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" className="h-8 px-2.5"
                        aria-label={nachOrdnern ? "Als flache Liste zeigen" : "Nach Ordnern gruppieren"}
                        title={nachOrdnern ? "Als flache Liste zeigen" : "Nach Ordnern gruppieren"}
                        data-testid="dok-ansicht"
                        onClick={() => setNachOrdnern((v) => !v)}>
                    {nachOrdnern ? <List size={14} /> : <Folder size={14} />}
                </Button>
                {nachOrdnern && (
                    <Button type="button" variant="ghost" size="sm"
                            className="h-8 px-2 text-xs text-gray-500"
                            data-testid="dok-alle-ordner"
                            onClick={alleUmschalten}>
                        {alleZu ? "Alle aufklappen" : "Alle zuklappen"}
                    </Button>
                )}
                <SuchAuswahl wert={filter.indexiert || "__alle__"} onWaehlen={(v) => setFilter({ ...filter, indexiert: v === "__alle__" ? "" : v })}
                    optionen={[
                        { wert: "__alle__", text: "Alle" },
                        { wert: "ja", text: "Nur indexierte" },
                        { wert: "nein", text: "Nur nicht indexierte" },
                    ]}
                    className="w-[170px]"
                    platzhalter="Indexierung"
                    testId="dok-filter-indexiert"
                />
                <Select value={filter.jahr || "__alle__"}
                        onValueChange={(v) => setFilter({ ...filter, jahr: v === "__alle__" ? "" : v })}>
                    <SelectTrigger className="h-8 w-[120px]" data-testid="dok-filter-jahr">
                        <SelectValue placeholder="Jahr" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__alle__">Alle Jahre</SelectItem>
                        {(auswahl.jahre || []).map((j) => (
                            <SelectItem key={j} value={String(j)}>{j}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.14em] text-gray-500">
                        <tr>
                            <th className="px-3 py-2 text-left">Dokument</th>
                            <th className="px-3 py-2 text-left">Art</th>
                            <th className="px-3 py-2 text-left">Sphäre</th>
                            <th className="px-3 py-2 text-right">Jahr</th>
                            <th className="px-3 py-2 text-right">Größe</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {nachOrdnern && zeilenFuer(baum, 0)}
                        {!nachOrdnern && sichtbar.map((d) => dateiZeile(d, 0, true))}
                        {!sichtbar.length && (
                            <tr><td colSpan={6} className="px-3 py-12 text-center text-sm text-gray-400">
                                {laedt ? "Lädt …"
                                    : items.length ? "Kein Treffer — Suche oder Filter blenden alles aus."
                                    : (drivOrdnerId
                                        ? 'Noch nichts verzeichnet. „Mit Drive abgleichen“ holt den Bestand.'
                                        : "Kein Drive-Ordner hinterlegt.")}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Dokumente;
